import momentTZ from 'moment-timezone';

export type Zone = { id: string; label: string };
export type ZoneGroup = { group: string; zones: Zone[] };

// Curated list and grouping from the WST-764 ticket HTML. The ticket left long-tail zones as raw
// IANA paths; per stakeholder request those were given friendly names too, for consistency.
export const TIMEZONE_GROUPS: ZoneGroup[] = [
  {
    group: 'US/Canada',
    zones: [
      { id: 'America/Los_Angeles', label: 'Pacific Time - US & Canada' },
      { id: 'America/Denver', label: 'Mountain Time - US & Canada' },
      { id: 'America/Chicago', label: 'Central Time - US & Canada' },
      { id: 'America/New_York', label: 'Eastern Time - US & Canada' },
      { id: 'America/Anchorage', label: 'Alaska Time' },
      { id: 'America/Phoenix', label: 'Arizona, Yukon Time' },
      { id: 'America/St_Johns', label: 'Newfoundland Time' },
      { id: 'Pacific/Honolulu', label: 'Hawaii Time' },
    ],
  },
  {
    group: 'America',
    zones: [
      { id: 'America/Adak', label: 'Aleutian Time' },
      { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires Time' },
      { id: 'America/Asuncion', label: 'Asuncion Time' },
      { id: 'America/Bogota', label: 'Bogota, Jamaica, Lima Time' },
      { id: 'America/Campo_Grande', label: 'Campo Grande Time' },
      { id: 'America/Caracas', label: 'Caracas Time' },
      { id: 'America/Godthab', label: 'Greenland Time' },
      { id: 'America/Halifax', label: 'Atlantic Time' },
      { id: 'America/Regina', label: 'Saskatchewan, Guatemala, Costa Rica Time' },
      { id: 'America/Havana', label: 'Cuba Time' },
      { id: 'America/Mazatlan', label: 'Mexican Pacific Time' },
      { id: 'America/Mexico_City', label: 'Mexico City Time' },
      { id: 'America/Montevideo', label: 'Montevideo Time' },
      { id: 'America/Miquelon', label: 'St. Pierre & Miquelon Time' },
      { id: 'America/Noronha', label: 'Fernando de Noronha Time' },
      { id: 'America/Santiago', label: 'Santiago Time' },
      { id: 'America/Santa_Isabel', label: 'Baja California Time' },
      { id: 'America/Puerto_Rico', label: 'Atlantic Standard Time' },
      { id: 'America/Sao_Paulo', label: 'Brasilia Time' },
    ],
  },
  {
    group: 'Africa',
    zones: [
      { id: 'Africa/Cairo', label: 'Egypt Time' },
      { id: 'Africa/Maputo', label: 'Central Africa Time' },
      { id: 'Africa/Lagos', label: 'West Africa Time' },
      { id: 'Africa/Windhoek', label: 'Namibia Time' },
    ],
  },
  {
    group: 'Asia',
    zones: [
      { id: 'Asia/Amman', label: 'Jordan Time' },
      { id: 'Asia/Baghdad', label: 'Baghdad, East Africa Time' },
      { id: 'Asia/Baku', label: 'Azerbaijan Time' },
      { id: 'Asia/Beirut', label: 'Lebanon Time' },
      { id: 'Asia/Damascus', label: 'Syria Time' },
      { id: 'Asia/Dhaka', label: 'Bangladesh Time' },
      { id: 'Asia/Dubai', label: 'Dubai Time' },
      { id: 'Asia/Gaza', label: 'Gaza Time' },
      { id: 'Asia/Irkutsk', label: 'Irkutsk Time' },
      { id: 'Asia/Bangkok', label: 'Indochina Time' },
      { id: 'Asia/Jerusalem', label: 'Israel Time' },
      { id: 'Asia/Kabul', label: 'Kabul Time' },
      { id: 'Asia/Karachi', label: 'Pakistan, Maldives Time' },
      { id: 'Asia/Kathmandu', label: 'Kathmandu Time' },
      { id: 'Asia/Kolkata', label: 'India, Sri Lanka Time' },
      { id: 'Asia/Krasnoyarsk', label: 'Krasnoyarsk Time' },
      { id: 'Asia/Omsk', label: 'Omsk Time' },
      { id: 'Asia/Rangoon', label: 'Myanmar Time' },
      { id: 'Asia/Shanghai', label: 'China, Singapore, Perth' },
      { id: 'Asia/Tehran', label: 'Tehran Time' },
      { id: 'Asia/Tokyo', label: 'Japan, Korea Time' },
      { id: 'Asia/Vladivostok', label: 'Vladivostok Time' },
      { id: 'Asia/Yakutsk', label: 'Yakutsk Time' },
      { id: 'Asia/Yekaterinburg', label: 'Yekaterinburg Time' },
      { id: 'Asia/Yerevan', label: 'Armenia Time' },
    ],
  },
  {
    group: 'Atlantic',
    zones: [
      { id: 'Atlantic/Azores', label: 'Azores Time' },
      { id: 'Atlantic/Cape_Verde', label: 'Cape Verde Time' },
    ],
  },
  {
    group: 'Australia',
    zones: [
      { id: 'Australia/Adelaide', label: 'Adelaide Time' },
      { id: 'Australia/Brisbane', label: 'Brisbane Time' },
      { id: 'Australia/Darwin', label: 'Darwin Time' },
      { id: 'Australia/Eucla', label: 'Eucla Time' },
      { id: 'Australia/Lord_Howe', label: 'Lord Howe Island Time' },
      { id: 'Australia/Perth', label: 'Western Australia Time' },
      { id: 'Australia/Sydney', label: 'Sydney, Melbourne Time' },
    ],
  },
  {
    group: 'UTC',
    zones: [{ id: 'UTC', label: 'UTC Time' }],
  },
  {
    group: 'Europe',
    zones: [
      { id: 'Europe/Berlin', label: 'Central European Time' },
      { id: 'Europe/Bucharest', label: 'Eastern European Time' },
      { id: 'Europe/London', label: 'UK, Ireland, Lisbon Time' },
      { id: 'Europe/Minsk', label: 'Minsk Time' },
      { id: 'Europe/Moscow', label: 'Moscow Time' },
      { id: 'Europe/Istanbul', label: 'Turkey Time' },
    ],
  },
  {
    group: 'Pacific',
    zones: [
      { id: 'Pacific/Apia', label: 'Samoa Time' },
      { id: 'Pacific/Auckland', label: 'Auckland Time' },
      { id: 'Pacific/Chatham', label: 'Chatham Islands Time' },
      { id: 'Pacific/Easter', label: 'Easter Island Time' },
      { id: 'Pacific/Fiji', label: 'Fiji Time' },
      { id: 'Pacific/Gambier', label: 'Gambier Islands Time' },
      { id: 'Pacific/Kiritimati', label: 'Line Islands Time' },
      { id: 'Pacific/Majuro', label: 'Marshall Islands Time' },
      { id: 'Pacific/Marquesas', label: 'Marquesas Islands Time' },
      { id: 'Pacific/Norfolk', label: 'Norfolk Island Time' },
      { id: 'Pacific/Noumea', label: 'New Caledonia Time' },
      { id: 'Pacific/Pago_Pago', label: 'American Samoa Time' },
      { id: 'Pacific/Pitcairn', label: 'Pitcairn Islands Time' },
      { id: 'Pacific/Tarawa', label: 'Gilbert Islands Time' },
      { id: 'Pacific/Tongatapu', label: 'Tonga Time' },
    ],
  },
];

const CONTINENTS = TIMEZONE_GROUPS.map((g) => g.group);

const BY_ID: Record<string, { label: string; group: string }> = {};
TIMEZONE_GROUPS.forEach((g) => g.zones.forEach((z) => (BY_ID[z.id] = { label: z.label, group: g.group })));

/** Friendly label + group for an IANA id; synthesizes both for ids not in the curated set. */
export function findZone(id: string): { id: string; label: string; group: string } {
  const hit = BY_ID[id];
  if (hit) return { id, label: hit.label, group: hit.group };
  const continent = id.split('/')[0];
  const group = CONTINENTS.includes(continent) ? continent : 'America';
  const label = id.replace(/_/g, ' ').replace(/^[^/]+\//, '');
  return { id, label, group };
}

/** Current wall-clock time in a zone, formatted like the mockup ("4:08pm"). */
export function currentTimeFor(id: string, now?: momentTZ.Moment): string {
  const m = now ? now.clone().tz(id) : momentTZ.tz(id);
  return m.format('h:mma');
}

/** Filter the curated groups by case-insensitive label substring; drops empty groups. */
export function filterByLabel(query: string): ZoneGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return TIMEZONE_GROUPS;
  return TIMEZONE_GROUPS.map((g) => ({
    group: g.group,
    zones: g.zones.filter((z) => z.label.toLowerCase().includes(q)),
  })).filter((g) => g.zones.length > 0);
}
